// 홈페이지(PC 웹) SNS 로그인·연동.
// SNS 로그인은 앱과 같은 콜백 페이지(/auth/{kakao,naver,google,apple}/callback)로 돌아온다.
// 콜백 주소를 새로 등록하지 않도록 OAuth state 로 홈페이지 로그인·연동을 구분하고, 끝나면 홈페이지로 돌려보낸다.
export const HOMEPAGE_SNS_STATE = "homepage";
// 휴대폰 로그인 후 SNS 계정 연동 (앱의 state=connect 와 같은 처리)
export const HOMEPAGE_SNS_CONNECT_STATE = "homepage-connect";
const ERROR_KEY = "homepageSnsLoginError";
const NOTICE_KEY = "homepageSnsNotice";
// state 뒤에 붙이는 무작위 값. 이 브라우저에서 시작한 요청인지 콜백에서 대조한다.
// (고정 state 면 남이 만든 콜백 링크로 피해자 계정에 남의 SNS 가 연동되거나 남의 계정으로 로그인된다)
const NONCE_KEY = "homepageSnsNonce";

// 앱 SNS 연동(join/sns-connect)도 같은 방식을 쓴다. ("connect.{nonce}")
// 연동은 로그인한 계정에 SNS 를 붙이므로, 고정 state 면 남이 만든 링크로 피해자 계정에 남의 SNS 가 연동된다.
export const APP_SNS_CONNECT_STATE = "connect";

// "homepage.{nonce}" → { kind: "homepage", nonce }. 무작위 값을 쓰는 state 가 아니면 null.
const parseState = (state) => {
  const match = String(state || "").match(/^(homepage|homepage-connect|connect)(?:\.([0-9a-f]+))?$/);
  return match ? { kind: match[1], nonce: match[2] || "" } : null;
};
const savedNonce = () => {
  try {
    return sessionStorage.getItem(NONCE_KEY) || "";
  } catch {
    return "";
  }
};
const isValidState = (state, kind) => {
  const parsed = parseState(state);
  return !!parsed && parsed.kind === kind && !!parsed.nonce && parsed.nonce === savedNonce();
};

// SNS 로그인을 시작할 때 쓸 state. 무작위 값을 만들어 저장해 둔다.
export const createHomepageSnsState = (kind) => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const nonce = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  try {
    sessionStorage.setItem(NONCE_KEY, nonce);
  } catch {
    // 저장하지 못하면 콜백에서 위조로 보고 막힌다.
  }
  return `${kind}.${nonce}`;
};

export const isHomepageSnsState = (state) => isValidState(state, HOMEPAGE_SNS_STATE);
export const isHomepageSnsConnectState = (state) => isValidState(state, HOMEPAGE_SNS_CONNECT_STATE);
export const isAppSnsConnectState = (state) => isValidState(state, APP_SNS_CONNECT_STATE);
// 연동 요청인지 (무작위 값과 상관없이 모양만). 앱 오류 처리에서 돌아갈 화면을 고를 때 쓴다.
export const looksLikeAppSnsConnectState = (state) => parseState(state)?.kind === APP_SNS_CONNECT_STATE;
// 홈페이지 state 모양인데 이 브라우저에서 시작한 요청이 아니다. 콜백이 처리하지 않고 돌려보낸다.
export const isForgedHomepageSnsState = (state) => {
  const kind = parseState(state)?.kind;
  return (kind === HOMEPAGE_SNS_STATE || kind === HOMEPAGE_SNS_CONNECT_STATE) && !isHomepageSnsState(state) && !isHomepageSnsConnectState(state);
};
// 앱 연동 state 인데 이 기기에서 시작한 요청이 아니다. (고정 "connect" 포함)
export const isForgedAppSnsConnectState = (state) => looksLikeAppSnsConnectState(state) && !isAppSnsConnectState(state);
// 앱 연동이 끝나면(성공·실패) 무작위 값을 지운다.
export const clearSnsStateNonce = () => {
  try {
    sessionStorage.removeItem(NONCE_KEY);
  } catch {
    // 무시
  }
};
// 홈페이지에서 시작한 SNS 콜백인지 (useApi 가 로그인 만료 때 앱 로그인 화면으로 보내지 않게)
export const looksLikeHomepageSnsState = (state) => {
  const kind = parseState(state)?.kind;
  return kind === HOMEPAGE_SNS_STATE || kind === HOMEPAGE_SNS_CONNECT_STATE;
};

// SNS 화면에서 취소해 인가 코드 없이 돌아왔을 때 보낼 홈페이지 화면. 홈페이지 요청이 아니면 null.
// (취소는 아무것도 바꾸지 않아 무작위 값은 보지 않는다)
export const homepageSnsCancelPath = (state) => {
  const parsed = parseState(state);
  if (parsed?.kind === HOMEPAGE_SNS_STATE) return "/homepage/login";
  if (parsed?.kind === HOMEPAGE_SNS_CONNECT_STATE) return "/homepage/sns-connect";
  return null;
};

const store = (key, message) => {
  try {
    sessionStorage.setItem(key, message);
  } catch {
    // sessionStorage 를 못 쓰면 문구 없이 돌아간다.
  }
};

const clearNonce = () => {
  try {
    sessionStorage.removeItem(NONCE_KEY);
  } catch {
    // 무시
  }
};

const take = (key) => {
  try {
    const message = sessionStorage.getItem(key) || "";
    sessionStorage.removeItem(key);
    return message;
  } catch {
    return "";
  }
};

export const useHomepageSnsLogin = () => {
  // 성공: 앱 화면처럼 안내 팝업을 띄우지 않고 홈페이지로 보낸다. (홈페이지에는 앱 팝업 스타일이 없다)
  // 비밀번호(PIN)가 없으면 홈페이지 화면(AppShell)이 readMyLine 으로 보고 설정 팝업을 띄운다.
  // 끝나면(성공·실패) 무작위 값을 지워 같은 콜백 주소를 다시 쓰지 못하게 한다.
  const finishHomepageSnsLogin = () => {
    clearNonce();
    return navigateTo("/homepage", { replace: true });
  };

  // 실패: 오류 문구를 남겨 두고 홈페이지 로그인 화면으로 보낸다. 로그인 화면이 토스트로 보여준다.
  const failHomepageSnsLogin = (message) => {
    clearNonce();
    store(ERROR_KEY, message || "로그인하지 못했어요. 다시 시도해 주세요.");
    return navigateTo("/homepage/login", { replace: true });
  };

  // 연동 성공: 홈으로 보내고, 홈 화면이 완료 문구를 토스트로 보여준다.
  const finishHomepageSnsConnect = () => {
    clearNonce();
    store(NOTICE_KEY, "SNS 계정을 연동했어요.");
    return navigateTo("/homepage", { replace: true });
  };

  // 연동 실패: SNS 연동 화면으로 돌려보내고, 그 화면이 오류 문구를 토스트로 보여준다.
  // 로그인이 만료돼(UNAUTHORIZED) 연동하지 못했으면 다시 로그인하게 로그인 화면으로 보낸다. (토큰은 useApi 가 이미 지웠다)
  const failHomepageSnsConnect = (message) => {
    clearNonce();
    if (message === "UNAUTHORIZED") {
      store(ERROR_KEY, "로그인이 만료됐어요. 다시 로그인해 주세요.");
      return navigateTo("/homepage/login", { replace: true });
    }
    store(ERROR_KEY, message || "SNS 계정을 연동하지 못했어요. 다시 시도해 주세요.");
    return navigateTo("/homepage/sns-connect", { replace: true });
  };

  // 로그인·SNS 연동 화면이 한 번만 꺼내 쓴다.
  const takeHomepageSnsLoginError = () => take(ERROR_KEY);
  // 홈 화면이 한 번만 꺼내 쓴다.
  const takeHomepageSnsNotice = () => take(NOTICE_KEY);

  return { finishHomepageSnsLogin, failHomepageSnsLogin, finishHomepageSnsConnect, failHomepageSnsConnect, takeHomepageSnsLoginError, takeHomepageSnsNotice };
};
